import { isNil, reduce } from 'lodash';
import React, { useCallback, useId, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Redirect, useLocation } from 'react-router';
import { Button, Card, CardHeader } from 'reactstrap';
import { gql, useMutation, useQuery } from 'urql';
import { Loading } from '.';
import { GraphDeepPartialNotListed, GraphDeepPartialNotListedAndOverride, GraphDeepPartialOverride, GraphType, Override } from '../types';
import { HacpackMutation, DangerousGoods, HacpackMutationTestPositionsArguments, HacpackQuery, HacpackQueryHandlingArguments, Handling, HandlingOverrideInput, Result, Segregation, Terminal, TerminalArea, TestPositionsResult } from '../types/graph';
import CenteredContainer from './CenteredContainer';
import LoadingUnitIdentifier from './LoadingUnitIdentifier';
import { GridLayout } from './ui';

type TerminalHandlingSegregationQueryData = GraphDeepPartialOverride<HacpackQuery, {
    handling: TerminalHandlingSegregationHandling
}>

type TerminalHandlingSegregationHandling = GraphDeepPartialNotListedAndOverride<Handling, 'id' | 'loadingUnitIdentifier' | 'checkedIn' | 'checkedOut' | 'area' | 'bay' | 'row' | 'tier', {
    terminal: TerminalHandlingSegregationTerminal,
    dangerousGoods: TerminalHandlingSegregationDangerousGoods[]
}>

type TerminalHandlingSegregationTerminalArea = GraphDeepPartialNotListed<TerminalArea, 'name'>
type TerminalHandlingSegregationDangerousGoods = GraphDeepPartialNotListed<DangerousGoods, 'limitedQuantity'>

type TerminalHandlingSegregationTerminalSegregation = GraphDeepPartialNotListed<Segregation, 'id' | 'loadingUnit1Identifier' | 'loadingUnit2Identifier' | 'class1' | 'class2' | 'row1' | 'row2' | 'bay1' | 'bay2' | 'area' | 'terminalName' | 'dateOfInput'>

type TerminalHandlingSegregationTerminalHandling = Exclude<TerminalHandlingSegregationHandling, 'terminal'>

type TerminalHandlingSegregationTerminal = GraphDeepPartialNotListedAndOverride<Terminal, 'name', {
    areas: TerminalHandlingSegregationTerminalArea[],
    handlings: TerminalHandlingSegregationTerminalHandling[],
    segregations: TerminalHandlingSegregationTerminalSegregation[]
}>

type TerminalHandlingSegregationQueryArguments = HacpackQueryHandlingArguments;

const placardsAndMarkingsQuery = gql<TerminalHandlingSegregationQueryData, TerminalHandlingSegregationQueryArguments>`
query handling($id: Long!) {
    handling (id: $id) {
        id
        loadingUnitIdentifier
        checkedIn
        checkedOut
        row
        bay
        area
        tier
        dangerousGoods {
            limitedQuantity
        }
        terminal {
            name
            areas {
                name
            }
            handlings (checkedIn: true) {
                id
                loadingUnitIdentifier
                row
                bay
                area
                tier
            }
            segregations {
                id
                loadingUnit1Identifier
                loadingUnit2Identifier
                class1
                class2
                row1
                row2
                bay1
                bay2
                area
                terminalName
                dateOfInput
            }
        }
    }
}`;

type TerminalHandlingSegregationProps = {
    handlingId: number
}

//const PlacardsAndMarkingsPositions = styled.div`
//min-height: 100%;
//display: flex;
//flex-direction: column;
//padding: 15px;
//gap: 15px;
//justify-content: space-between;

//h5 {
//    position: sticky;
//    top: 0;
//    background-color: ${({ theme: { white } }) => white};
//    margin: 0;
//    padding: 6px 0;
//}

//`;

//const PlacardsAndMarkingsPosition = styled.div`
//display: flex;
//flex-direction: column;
//flex-wrap: wrap;
//justify-content: space-evenly;

//gap: 15px;
//`;

//const PlacardsAndMarkingsAlternatives = styled.div`

//background-color: ${({ theme: { gray300 } }) => gray300};

//display: flex;
//flex-direction: column;
//gap: 1em;
//padding: 15px;

//> div {
//    display: flex;
//    flex-direction: row;
//    flex-wrap: wrap;
//    align-content: stretch;
//    gap: 15px;

//    > div {
//    }
//}

//`;

//const PlacardsAndMarkingsGroups = styled.div`
//display: flex;
//flex-direction: row;
//flex-wrap: wrap;
//align-items: center;
//gap: 1em;

//> div {
//    display: flex;
//    flex-direction: row;
//    flex-wrap: wrap;
//    background-color: ${({ theme: { white } }) => white};

//    > div {
//        flex: 1;
//    }
//}
//`;

//const PlacardsAndMarkingsPlacard = styled.div`
//padding: 15px;
//> svg {
//    height: 120px;
//    width: 120px;
//}
//`;

//const PlacardsAndMarkingsMarking = styled.div`
//padding: 15px;
//p {
//    font-family: ${({ theme: { fontFamilyMonospace } }) => fontFamilyMonospace};
//    margin: 0;
//}
//`;

//const PlacardsAndMarkingsDisclaimer = styled(Alert)`
//margin-bottom: 0;
//`;

type TerminalHandlingSegregationTestMutationData = GraphType<HacpackMutation> & {
    testPositions: TestPositionsResult
};

type TerminalHandlingSegregationTestArguments = HacpackMutationTestPositionsArguments;
//handlingOverrides: Array < HandlingOverrideInput >

const testPositionsMutation = gql<TerminalHandlingSegregationTestMutationData, TerminalHandlingSegregationTestArguments>`mutation ($terminalName: String!, $handlingOverrides: [HandlingOverrideInput!]!) {
    testPositions (terminalName: $terminalName, handlingOverrides: $handlingOverrides)  {
        isOk
        data {
            id
            loadingUnit1Identifier
            loadingUnit2Identifier
            class1
            class2
            row1
            row2
            bay1
            bay2
            area
            terminalName
            dateOfInput
        }
    }
}`;

type TestPositionsFormValues = Pick<TerminalHandlingSegregationTestArguments, 'terminalName'> & HandlingOverrideInput;

type UnitPosition = {
    unitNumber: string,
    area: string,
    bay: string,
    row: string,
    hazardClasses: string[]
}

const __convertBay = (bay: number) => {
    const b = bay * .5

    if (bay % 2 === 1) {
        return [Math.floor(b)];
    }

    return [b - 1, b]
}

const __postionString = (area: string, bay: number, row: number, tier: number) => `${area} ${bay} ${row} ${tier}`;

const TerminalHandlingSegregation = ({ handlingId: id }: TerminalHandlingSegregationProps) => {

    const [queryResult, repeatQuery] = useQuery<TerminalHandlingSegregationQueryData, TerminalHandlingSegregationQueryArguments>({
        query: placardsAndMarkingsQuery,
        variables: { id },
        requestPolicy: 'cache-and-network'
    });

    const [testPositionResult, testPositions] = useMutation(testPositionsMutation);

    const { fetching: mutationFetching } = testPositionResult;

    const [testPositionResultAndData, setTestPositionResultAndData] = useState<{ result: TestPositionsResult, data: TestPositionsFormValues } | null>(null);

    const { t } = useTranslation('common');

    const { data, fetching, error } = queryResult;

    const { register, handleSubmit, formState, watch } = useForm<TestPositionsFormValues>({ defaultValues: { handlingId: id } });

    const { state: { parentUrl } } = useLocation<{ parentUrl: string }>();

    //const { } = formState;

    const values = watch(['area', 'bay', 'row', 'tier']);

    const uid = useId();

    const limitedQuantity = useMemo(() => {
        var dangerousGoods = data?.handling?.dangerousGoods;

        if (isNil(dangerousGoods)) {
            return null;
        }

        return dangerousGoods.every(dg => dg?.limitedQuantity ?? false);

    }, [data?.handling, data?.handling?.dangerousGoods]);

    const { handlingsByPosition, handlingsByUnitNumber } = useMemo(() => {
        const handlingsByUnitNumber = new Map<string, TerminalHandlingSegregationTerminalHandling>();
        const handlingsByPosition = new Map<string, TerminalHandlingSegregationTerminalHandling>();

        if (data === undefined) {
            return { handlingsByPosition, handlingsByUnitNumber };
        }

        const handlingId = data.handling.id;

        const handlings = data.handling.terminal.handlings as TerminalHandlingSegregationTerminalHandling[];

        if (isNil(handlingId) || handlings === undefined) {
            return { handlingsByPosition, handlingsByUnitNumber };
        }


        const l = handlings.length;

        for (let i = 0; i < l; i++) {
            const handling = handlings[i];
            //const id = handling.id!;

            const unitNumber = handling.loadingUnitIdentifier!;

            handlingsByUnitNumber.set(unitNumber, handling);

            if (handlingId === handling.id || isNil(handling.area)) {
                continue;
            }

            const area = handling.area.trim();
            const bay = Number(handling.bay);
            const row = Number(handling.row);
            const tier = Number(handling.tier)

            if (isNaN(row) || isNaN(bay) || isNaN(tier)) {
                continue;
            }

            //const areaMap = areasMap.get(area) ?? areasMap.set(area, new Map<number, Map<number, Map<number, number>>>()).get(area)!;

            //const rowMap = areaMap.get(row) ?? areaMap.set(row, new Map<number, Map<number, number>>()).get(row)!;

            const bays = __convertBay(bay);

            for (let j = 0; j < bays.length; j++) {
                handlingsByPosition.set(__postionString(area, bays[j], row, tier), handling);
            }
        }

        return { handlingsByPosition, handlingsByUnitNumber };

    }, [data?.handling, data?.handling.terminal.handlings]);



    const areasDatalist = useMemo(() => {
        if (data === undefined) {
            return null;
        }

        const areas = data?.handling.terminal.areas as Pick<TerminalArea, 'name'>[];

        return <datalist id={`${uid}areasDataList`}>{areas.map(area => <option key={area.name} value={area.name} ></option>)}</datalist>
    }, [data?.handling.terminal.areas, uid]);



    const [positionIsValid, positionConflicts] = useMemo(() => {
        const area = values[0]?.trim();
        const positionConflicts = new Set<TerminalHandlingSegregationTerminalHandling>()

        if (isNil(area) || area.trim().length === 0
        ) {
            return [false, positionConflicts];
        }

        const bay = Number.parseInt(values[1] ?? "");
        const row = Number.parseInt(values[2] ?? "");
        const tier = Number.parseInt(values[3] ?? "");

        if (isNaN(row) || isNaN(bay) || isNaN(tier)) {
            return [false, positionConflicts];
        }

        const bays = __convertBay(bay);



        for (let j = 0; j < bays.length; j++) {

            const positionString = __postionString(area, bays[j], row, tier);

            if (handlingsByPosition.has(positionString)) {
                positionConflicts.add(handlingsByPosition.get(positionString)!)
            }
        }

        return [true, positionConflicts];
    }, [values, handlingsByPosition]);

    const positionExists = positionConflicts.size > 0;

    const terminalName = data?.handling.terminal.name;

    const onSubmit: SubmitHandler<TestPositionsFormValues> = useCallback(async (formData) => {
        try {

            setTestPositionResultAndData(null)

            if (isNil(terminalName)) {
                return;
            }

            const { data } = await testPositions({ terminalName, handlingOverrides: [formData] });

            if (data === undefined) {
                setTestPositionResultAndData(null);
                return;
            }

            setTestPositionResultAndData({ result: data.testPositions, data: formData });
        } finally {
            repeatQuery();
        }
    }, [terminalName, setTestPositionResultAndData, repeatQuery]);


    //const handling = data.handling;
    //const terminal = handling.terminal;
    //const areas = terminal.areas.filter((area): area is TerminalHandlingSegregationTerminalArea => area !== undefined);
    //const segregations = terminal.areas.filter((area): area is TerminalHandlingSegregationTerminalArea => area !== undefined);

    const header = useMemo(() => {
        return <></>
    }, [])

    const form = useMemo(() => {
        if (data === undefined) {
            return null;
        }

        const handling = data.handling;

        return <form onSubmit={handleSubmit(onSubmit)} className="pt-2">
            <div className="p-2">
                <div className="d-none">
                    {areasDatalist}
                    <input type="hidden" {...register('handlingId', { valueAsNumber: true })} />
                </div>

                <div>
                    <label htmlFor={`${uid}area`}>Area</label>
                    <input {...register('area')} className="form-control" type="text" id={`${uid}area`} list={`${uid}areasDataList`} />
                    {!isNil(handling.area) && handling.area.trim().length > 0 && <div className="form-text">Area attuale: {handling.area}</div>}
                </div>

                <div>
                    <label htmlFor={`${uid}bay`}>Baia</label>
                    <input {...register('bay')} className="form-control" type="text" id={`${uid}bay`} />
                    {!isNil(handling.bay) && handling.bay.trim().length > 0 && <div className="form-text">Baia attuale: {handling.bay}</div>}
                </div>

                <div>
                    <label htmlFor={`${uid}row`}>Fila</label>
                    <input {...register('row')} className="form-control" type="text" id={`${uid}row`} />
                    {!isNil(handling.row) && handling.row.trim().length > 0 && <div className="form-text">Fila attuale: {handling.row}</div>}
                </div>

                <div>
                    <label htmlFor={`${uid}tier`}>Tiro</label>
                    <input {...register('tier')} className="form-control" type="text" id={`${uid}tier`} />
                    {!isNil(handling.tier) && handling.tier.trim().length > 0 && <div className="form-text">Tiro attuale: {handling.tier}</div>}
                </div>

                <div>
                    <Button disabled={!positionIsValid || positionExists} className="mt-3" type="submit">Test posizione</Button>
                </div>
                {positionExists && <small className="form-text text-muted">
                    Questa posizione è già occupata ({Array.from(positionConflicts).map((conflict, i) =>
                        <>{i !== 0 && ', '}<LoadingUnitIdentifier identifier={conflict.loadingUnitIdentifier} /></>
                    )}).
                </small>}
            </div>
            {/*<pre>{JSON.stringify(formState, null, 2)}</pre>*/}
        </form>
    }, [areasDatalist, register, uid, handleSubmit, onSubmit, data?.handling, positionIsValid, positionExists, positionConflicts]);

    const resultTable = useMemo(() => {

        if (mutationFetching) {
            return <Loading />
        }

        if (data === undefined) {
            return null;
        }



        if (testPositionResultAndData === null) {
            return <div className="alert alert-light" role="alert">Seleziona posizione da testare.</div>
        }

        const { data: formData, result } = testPositionResultAndData;

        const loadingUnitIdentifier = data.handling.loadingUnitIdentifier;

        const closeButton = <button type="button" className="btn-close" onClick={() => setTestPositionResultAndData(null)} aria-label="Close"></button>;

        if (result.isOk === false) {
            return <div className="alert alert-danger" role="alert">
                Si è verificato un errore nell'elaborazione dei dati.
                {closeButton}
            </div>
        }

        const segregationProblems = reduce(result.data, (acc, sp) => {
            switch ([sp.loadingUnit1Identifier, sp.loadingUnit2Identifier].indexOf(loadingUnitIdentifier)) {
                case 0:
                    acc.push({
                        unitNumber: sp.loadingUnit2Identifier!,
                        area: sp.area!,
                        bay: sp.bay2!,
                        row: sp.row2!,
                        hazardClasses: sp.class2!.split(':')
                    });
                    break;

                case 1:

                    acc.push({
                        unitNumber: sp.loadingUnit1Identifier!,
                        area: sp.area!,
                        bay: sp.bay1!,
                        row: sp.row1!,
                        hazardClasses: sp.class1!.split(':')
                    });
                    break;
            }

            return acc;
        }, [] as UnitPosition[])

        if (segregationProblems.length === 0) {
            return <div className="alert alert-success" role="alert">
                Nessun problema di segregazione per container
                {' '}<strong><LoadingUnitIdentifier identifier={loadingUnitIdentifier} /></strong>{' '}
                se posizionato in area <strong>{formData.area}</strong>, baia <strong>{formData.bay}</strong>,
                fila <strong>{formData.row}</strong> e tiro <strong>{formData.tier}</strong>.
                {closeButton}
            </div>
        }

        return <Card color="warning">
            <CardHeader>
                Problemi di segregazione
                {' '}<strong><LoadingUnitIdentifier identifier={loadingUnitIdentifier} /></strong>{' '}
                se posizionato in area <strong>{formData.area}</strong>, baia <strong>{formData.bay}</strong>,
                fila <strong>{formData.row}</strong> e tiro <strong>{formData.tier}</strong>.
                {closeButton}
            </CardHeader>
            <div className="card-body p-0">
                <table className="table table-striped">
                    <thead>
                        <tr>
                            <th>Sigla</th>
                            <th>Area</th>
                            <th>Baia</th>
                            <th>Fila</th>
                            <th>Classi</th>
                        </tr>
                    </thead>

                    <tbody>
                        {segregationProblems.map(sp => <tr>
                            <td>{sp.unitNumber}</td>
                            <td>{sp.area}</td>
                            <td>{sp.bay}</td>
                            <td>{sp.row}</td>
                            <td>{sp.hazardClasses.join(', ')}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
        </Card>

    }, [testPositionResultAndData, data, handlingsByUnitNumber, mutationFetching]);

    if (fetching === true || data === undefined) {
        return <Loading />;
    }

    if (!['TDT', 'MEDCENTER'].includes(data.handling.terminal.name!)) {
        return <Redirect to={parentUrl} />;
    }

    if (limitedQuantity) {
        return <CenteredContainer><div className="alert alert-success">Tutte le merci pericolose nell'unità sono in quantità limitata.</div></CenteredContainer>
    }

    return <GridLayout header={header} left={form}>
        <CenteredContainer>{resultTable}</CenteredContainer>
    </GridLayout>

};

export default TerminalHandlingSegregation;